import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";

import WelcomeScreen from "./components/screens/WelcomeScreen";
import ExplScreen from "./components/screens/ExplScreen";
import EndScreen from "./components/screens/EndScreen";
import LanguageScreen from "./components/screens/LanguageScreen";

import LevelScreen from "./components/general/LevelUp";

import { Context } from "./provider/index";
import Header from "./components/general/Header";
import Footer from "./components/general/Footer";
import PickerScreen from "./components/screens/PickerScreen";

import { BadBoxesBase, GoodBoxesBase, Corners } from "./LEVELS.js";

const snakeSize = 3.5; //3%;
const wordSize = 3; //2%
const maxLevels = 6;
const wallDelay = -2;
const distanceDefault = 0.3;
const moveDelay = 100;

let lastMoveTime = 0;

const Snake = () => {
  const { admin, dispatch } = useContext(Context);

  const [s, setStage] = useState(0);
  const [l, setLevel] = useState(1);
  const [gameStage, setGameStage] = useState("to-start");

  const distance = useRef(distanceDefault);
  const delay = useRef(true);
  const blink = useRef(true);
  const BadBoxes = useRef({ ...BadBoxesBase });
  const GoodBoxes = useRef({ ...GoodBoxesBase });

  const gs = useRef(gameStage);
  const stage = useRef();
  const level = useRef(0);
  const wallOpacity = useRef(wallDelay);
  const currentAdmin = useRef();
  const requestRef = useRef();
  const gameOver = useRef(false);

  const patternHorImg = useRef();
  const patternVerImg = useRef();
  const snakeHeadImg = useRef();
  const speedImg = useRef();
  const slowImg = useRef();

  const music1 = useRef();
  const music2 = useRef();
  const musicReady = useRef(false);
  const snake = useRef();
  const timer = useRef();
  const playTime = useRef();
  const blinkTimer = useRef();
  const speedTimer = useRef();
  const direction = useRef();
  const canvas = useRef();
  const ctx = useRef();
  const rerender = useRef(true);

  const [gameSize, setSize] = useState({
    width: admin.size.w,
    height: admin.size.w,
  });

  useEffect(() => {
    Preload((val) => (snakeHeadImg.current = val), "/images/logo/logo.png");
    Preload(
      (val) => (speedImg.current = val),
      "/images/gameElements/speed.png"
    );
    Preload((val) => (slowImg.current = val), "/images/gameElements/slow.png");

    Preload(
      (val) => (patternHorImg.current = val),
      "/images/borders/horizontal.png"
    );
    Preload(
      (val) => (patternVerImg.current = val),
      "/images/borders/vertical.png"
    );

    Resize();

    // Check if the audio is loaded
    if (music1.current) {
      music1.current.load();
    }

    if (music2.current) {
      music2.current.load();
    }

    window.addEventListener("resize", Resize, { passive: true });
    window.addEventListener("keyup", KeyPressed, { passive: true });
    window.addEventListener("keydown", CheckReset, { passive: true });

    return () => {
      cancelAnimationFrame(requestRef.current);

      window.removeEventListener("resize", Resize, { passive: true });
      window.removeEventListener("keyup", KeyPressed, { passive: true });
      window.removeEventListener("keyup", KeyPressed, { passive: true });
    };
  }, []);

  useEffect(() => {
    stage.current = s;

    gs.current = gameStage;

    if (s == 4 && gameStage == "started") {
      canvas.current.width = gameSize.width;
      canvas.current.height = gameSize.height;

      ctx.current = canvas.current.getContext("2d");
      wallOpacity.current = wallDelay;

      console.log("started");

      resetGame(true, true);

      draw();

      playTime.current = new Date().getTime();
    }
  }, [s, gameStage]);

  useEffect(() => {
    if (level.current != l) {
      level.current = l;

      resetGame();

      dispatch({ type: "level", payload: l });
    }
  }, [l]);

  useEffect(() => {
    currentAdmin.current = admin;
  }, [admin]);

  useEffect(() => {
    if (canvas.current) {
      canvas.current.width = gameSize.width;
      canvas.current.height = gameSize.height;
    }
  }, [gameSize.width, gameSize.height]);

  /*--------------------------
    BASE SETUP
  --------------------------*/
  const TimerReset = useRef();
  const pressedKeys = useRef({});

  const CheckReset = ({ keyCode }) => {
    if (!musicReady.current && music1.current && music2.current) {
      musicReady.current = true;
      music1.current.play();
      music2.current.play();

      setTimeout(() => {
        music1.current.pause();
        music1.current.currentTime = 0;
        music2.current.pause();
        music2.current.currentTime = 0;
      }, 1);
    }

    if ((keyCode === 37 || keyCode === 81) && !pressedKeys.current["left"]) {
      ControllerLogic("left");
    } else if (
      (keyCode === 39 || keyCode === 83) &&
      !pressedKeys.current["right"]
    ) {
      ControllerLogic("right");
    } else if (
      (keyCode === 38 || keyCode === 68) &&
      !pressedKeys.current["up"]
    ) {
      ControllerLogic("up");
    } else if (
      (keyCode === 40 || keyCode === 70) &&
      !pressedKeys.current["down"]
    ) {
      ControllerLogic("down");
    } else if (
      (keyCode === 32 || keyCode === 13 || keyCode === 71) &&
      !pressedKeys.current["next"]
    ) {
      ControllerLogic("next");
    }
  };

  const Resize = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      const newW = window.innerWidth * 0.85;

      dispatch({ type: "size", payload: { w: newW, h: newW } });

      setSize({ ...{ width: newW, height: newW } });
    }, 350);
  };

  /*--------------------------
    CONTROLS
  --------------------------*/
  const KeyPressed = ({ keyCode }) => {
    if (keyCode === 37 || keyCode === 81) {
      pressedKeys.current["left"] = false;
    } else if (keyCode === 39 || keyCode === 83) {
      pressedKeys.current["right"] = false;
    } else if (keyCode === 38 || keyCode === 68) {
      pressedKeys.current["up"] = false;
    } else if (keyCode === 40 || keyCode === 70) {
      pressedKeys.current["down"] = false;
    } else if (keyCode === 32 || keyCode === 13 || keyCode === 71) {
      pressedKeys.current["next"] = false;
    }
  };

  const WTF = (obj) => {
    const v = { ...obj };

    for (let parentId in v) {
      v[parentId] = [
        ...obj[parentId].map((child, childId) => ({ ...child, active: true })),
      ];
    }

    return v;
  };

  const ControllerLogic = useCallback(
    (type) => {
      pressedKeys.current[type] = true;

      if (TimerReset.current) {
        clearTimeout(TimerReset.current);
      }

      TimerReset.current = setTimeout(() => {
        if (pressedKeys.current["left"] && pressedKeys.current["next"]) {
          setStage(0);
          setLevel(1);

          gameOver.current = true;

          cancelAnimationFrame(requestRef.current);

          setTimeout(() => {
            resetGame();
            setGameStage("to-start");

            GoodBoxes.current = WTF(GoodBoxesBase);
            BadBoxes.current = WTF(BadBoxesBase);
          }, 1000);

          dispatch({ type: "reset" });
        }
      }, 5000);

      switch (type) {
        case "next":
          if (stage.current < 4) {
            if (currentAdmin.current.rulesToSwitch) {
              dispatch({
                type: "option",
                payload:
                  currentAdmin.current.option + 1 <
                  currentAdmin.current.maxOptions
                    ? currentAdmin.current.option + 1
                    : 0,
              });
              dispatch({ type: "activeButton", payload: "right" });

              if (
                currentAdmin.current.option + 1 ==
                currentAdmin.current.maxOptions
              ) {
                setStage(stage.current + 1);
              }
            } else if (!currentAdmin.current.rulesToSwitch) {
              setStage(stage.current + 1);
            }
          } else if (stage.current == 4) {
            if (gs.current == "to-start") {
              setGameStage("started");
            }
          }
          break;

        case "left":
          dispatch({ type: "activeButton", payload: "left" });

          if (stage.current == 4) {
            direction.current = { x: -1, y: 0, rotate: -90 };
          } else {
            dispatch({
              type: "option",
              payload:
                currentAdmin.current.option - 1 >= 0
                  ? currentAdmin.current.option - 1
                  : currentAdmin.current.maxOptions - 1,
            });
          }
          break;

        case "right":
          dispatch({ type: "activeButton", payload: "right" });

          if (stage.current == 4) {
            direction.current = { x: 1, y: 0, rotate: 90 };
          } else {
            dispatch({
              type: "option",
              payload:
                currentAdmin.current.option + 1 <
                currentAdmin.current.maxOptions
                  ? currentAdmin.current.option + 1
                  : 0,
            });
          }
          break;

        case "up":
          dispatch({ type: "activeButton", payload: "up" });

          if (stage.current == 4) {
            direction.current = { x: 0, y: -1, rotate: 0 };
          } else {
            dispatch({
              type: "option",
              payload:
                currentAdmin.current.option - 1 >= 0
                  ? currentAdmin.current.option - 1
                  : currentAdmin.current.maxOptions - 1,
            });
          }
          break;

        case "down":
          dispatch({ type: "activeButton", payload: "down" });

          if (stage.current == 4) {
            direction.current = { x: 0, y: 1, rotate: 180 };
          } else {
            dispatch({
              type: "option",
              payload:
                currentAdmin.current.option + 1 <
                currentAdmin.current.maxOptions
                  ? currentAdmin.current.option + 1
                  : 0,
            });
          }
          break;

        default:
          break;
      }
    },
    [stage, admin]
  );

  const blinkAnimation = () => {
    blinkTimer.current = setTimeout(() => {
      blink.current = !blink.current;
      rerender.current = true;

      wallOpacity.current += 0.33;

      if (wallOpacity.current > 1) {
        wallOpacity.current = 1;
      }

      if (delay.current) {
        blinkAnimation();
      }
    }, 120);
  };

  const resetGame = (b = false, wallStart = false) => {
    direction.current = { x: 0, y: -1, rotate: 0 };
    distance.current = distanceDefault;
    gameOver.current = false;
    delay.current = false;

    if (canvas.current) {
      snake.current = {
        x: (canvas.current.width - snakeSize) / 2,
        y: (canvas.current.height - snakeSize) / 2,
      };
    }

    if (speedTimer.current) {
      clearTimeout(speedTimer.current);
    }

    lastMoveTime = 0;

    if (b && !delay.current) {
      delay.current = true;

      if (blinkTimer.current) {
        clearTimeout(blinkTimer.current);
      }

      blinkAnimation();

      setTimeout(() => {
        delay.current = false;
        wallOpacity.current = 1;
      }, 2000 + (wallStart ? 2000 : 0));
    }

    dispatch({ type: "mode", payload: "normal" });
  };

  const Preload = (callbackVar, img) => {
    const imageLoader = new Image();
    imageLoader.onload = function () {
      callbackVar(this);
    };

    imageLoader.onerror = function () {
      console.error("Error loading: ", img);
    };

    imageLoader.src = img;
  };

  const moveSnake = useCallback(() => {
    if (Date.now() - lastMoveTime > moveDelay) {
      let newX =
        snake.current.x +
        direction.current.x *
          ((snakeSize / 100) * canvas.current.width) *
          distance.current;
      let newY =
        snake.current.y +
        direction.current.y *
          ((snakeSize / 100) * canvas.current.width) *
          distance.current;

      if (newX >= canvas.current.width) {
        newX = 0;
      } else if (newX <= -((snakeSize / 100) * canvas.current.width)) {
        newX = canvas.current.width - (snakeSize / 100) * canvas.current.width;
      }

      if (newY >= canvas.current.height) {
        newY = 0;
      } else if (newY <= -((snakeSize / 100) * canvas.current.width)) {
        newY = canvas.current.height - (snakeSize / 100) * canvas.current.width;
      }

      snake.current = { x: newX, y: newY };

      rerender.current = true;

      lastMoveTime = Date.now();
    }
  }, [gameSize.width, gameSize.height]);

  const checkCollisions = (x, y) => {
    let levelRunningCount = 0;

    Corners.forEach((rect) => {
      if (rectIntersect({ x, y }, rect)) {
        if (!delay.current) {
          delay.current = true;

          if (blinkTimer.current) {
            clearTimeout(blinkTimer.current);
          }

          blinkAnimation();

          setTimeout(CheckIfLivesLeft, 1200);
        }
      }
    });

    BadBoxes.current[`level${level.current}`].forEach((rect) => {
      if (rect.word[currentAdmin.current.language]) {
        rect.w =
          wordSize * rect.word[currentAdmin.current.language].length * 0.88;
        rect.h = wordSize;
      }

      if (rectIntersect({ x, y }, rect)) {
        if (!delay.current) {
          delay.current = true;

          if (blinkTimer.current) {
            clearTimeout(blinkTimer.current);
          }

          blinkAnimation();

          setTimeout(CheckIfLivesLeft, 1200);
        }
      }
    });

    GoodBoxes.current[`level${level.current}`].forEach((rect, k) => {
      if (
        typeof rect.word != "undefined" &&
        typeof rect.word[currentAdmin.current.language] != "undefined" &&
        rect.active
      ) {
        rect.w =
          wordSize * rect.word[currentAdmin.current.language].length * 0.88;
        rect.h = wordSize;

        levelRunningCount++;
      }

      if (rectIntersect({ x, y }, rect)) {
        if ((rect.type == "speed" || rect.type == "slow") && rect.active) {
          GoodBoxes.current[`level${level.current}`][k].active = false;

          distance.current =
            rect.type == "speed" ? distanceDefault * 3 : distanceDefault / 2;

          dispatch({ type: "mode", payload: rect.type });

          if (speedTimer.current) {
            clearTimeout(speedTimer.current);
          }

          speedTimer.current = setTimeout(() => {
            distance.current = distanceDefault;

            dispatch({ type: "mode", payload: "normal" });
          }, 8000);
        } else if (
          rect.word &&
          rect.word[currentAdmin.current.language] &&
          rect.active
        ) {
          GoodBoxes.current[`level${level.current}`][k].active = false;

          levelRunningCount--;
        }
      }
    });

    if (levelRunningCount == 0 && !gameOver.current) {
      gameOver.current = true;

      cancelAnimationFrame(requestRef.current);

      setTimeout(() => {
        dispatch({
          type: "time",
          payload: (Date.now() - playTime.current - 2000) / 1000,
        });

        resetGame(true);

        setLevel((lv) => {
          if (music1.current) {
            music1.current.play();
          }

          if (lv + 1 <= maxLevels) {
            return lv + 1;
          } else {
            setStage(6);

            if (music2.current) {
              music2.current.play();
            }

            dispatch({
              type: "final",
            });

            return 6;
          }
        });

        setGameStage("to-start");
      }, 500);
    }
  };

  const CheckIfLivesLeft = () => {
    delay.current = false;

    if (currentAdmin.current.lives > 1) {
      dispatch({ type: "lives", payload: currentAdmin.current.lives - 1 });

      resetGame(true);
    } else {
      gameOver.current = true;

      dispatch({ type: "lives", payload: currentAdmin.current.lives - 1 });

      setTimeout(() => {
        cancelAnimationFrame(requestRef.current);

        setGameStage("game-over");

        setStage(5);
      }, 500);
    }
  };

  const rectIntersect = (pos, rect) => {
    rect = {
      x: (canvas.current.width * rect.x) / 100,
      y: (canvas.current.height * rect.y) / 100,
      w: (canvas.current.width * rect.w) / 100,
      h: (canvas.current.height * rect.h) / 100,
    };

    return (
      pos.x < rect.x + rect.w &&
      pos.x + (snakeSize / 100) * canvas.current.width > rect.x &&
      pos.y < rect.y + rect.h &&
      pos.y + (snakeSize / 100) * canvas.current.width > rect.y
    );
  };

  const draw = useCallback(() => {
    //Calculate next position
    if (!delay.current && !gameOver.current) {
      blink.current = false;

      moveSnake();
    } else {
      rerender.current = true;
    }

    //Draw all items on canvas
    DrawGameElements();

    if (!gameOver.current) {
      requestRef.current = requestAnimationFrame(draw);
    }
  }, [gameSize.width, gameSize.height]);

  const DrawSnakeHead = (x, y) => {
    ctx.current.save();

    if (!blink.current) {
      ctx.current.translate(
        x + ((snakeSize / 100) * canvas.current.width) / 2,
        y + ((snakeSize / 100) * canvas.current.width) / 2
      );
      ctx.current.rotate((direction.current.rotate * Math.PI) / 180);

      ctx.current.drawImage(
        snakeHeadImg.current,
        -(((snakeSize / 100) * canvas.current.width) / 2),
        -(((snakeSize / 100) * canvas.current.width) / 2),
        (snakeSize / 100) * canvas.current.width,
        (snakeSize / 100) * canvas.current.width
      );
    }

    ctx.current.restore();

    //Collision with playing field?
    if (!gameOver.current) {
      checkCollisions(x, y);
    }
  };

  const DrawGameElements = useCallback(() => {
    if (rerender.current) {
      //Everything snake related
      ctx.current.clearRect(0, 0, canvas.current.width, canvas.current.height);

      rerender.current = false;

      if (snakeHeadImg.current) {
        // Draw snake head (assuming snakeHeadImg is a loaded image object)
        const headX = snake.current.x;
        const headY = snake.current.y;

        DrawSnakeHead(headX, headY);

        //out of field, create duplicate for smooth transition over borders
        if (
          headX < 0 ||
          headY < 0 ||
          headX + (snakeSize / 100) * canvas.current.width >
            canvas.current.width ||
          headY + (snakeSize / 100) * canvas.current.width >
            canvas.current.height
        ) {
          const x =
            headX < 0
              ? canvas.current.width + headX
              : headX + (snakeSize / 100) * canvas.current.width >
                canvas.current.width
              ? headX - canvas.current.width
              : headX;
          const y =
            headY < 0
              ? canvas.current.height + headY
              : headY + (snakeSize / 100) * canvas.current.width >
                canvas.current.height
              ? headY - canvas.current.height
              : headY;

          if (x != headX && y != headY) {
            DrawSnakeHead(x, y);
          }

          if (x != headX) {
            DrawSnakeHead(x, headY);
          }

          if (y != headY) {
            DrawSnakeHead(headX, y);
          }
        }
      }

      //Everything field related
      if (patternHorImg.current && patternVerImg.current) {
        BadBoxes.current[`level${level.current}`].forEach((rect) => {
          if (rect.word && rect.word[currentAdmin.current.language]) {
            ctx.current.fillStyle = `rgb(239, 62, 51)`;
            ctx.current.font = `${Math.round(
              (canvas.current.width * wordSize) / 100
            )}px Joystix`;
            ctx.current.fillText(
              rect.word[currentAdmin.current.language],
              canvas.current.width * (rect.x / 100),
              canvas.current.height * (rect.y / 100) +
                Math.round(((canvas.current.width * wordSize) / 100) * 0.85)
            );
          } else {
            WallGeneration(rect);
          }
        });

        Corners.forEach((rect) => WallGeneration(rect));
      }

      if (speedImg.current && slowImg.current) {
        GoodBoxes.current[`level${level.current}`].forEach((rect) => {
          if (rect.type && rect.active) {
            const img =
              rect.type == "speed" ? speedImg.current : slowImg.current;
            ctx.current.drawImage(
              img,
              canvas.current.width * (rect.x / 100),
              canvas.current.height * (rect.y / 100),
              canvas.current.width * (rect.w / 100),
              canvas.current.height * (rect.h / 100)
            );
          } else if (
            rect.word &&
            rect.word[currentAdmin.current.language] &&
            rect.active
          ) {
            ctx.current.fillStyle = "rgb(255, 255, 255)";
            ctx.current.font = `${Math.round(
              (canvas.current.width * wordSize) / 100
            )}px Joystix`;
            ctx.current.fillText(
              rect.word[currentAdmin.current.language],
              canvas.current.width * (rect.x / 100),
              canvas.current.height * (rect.y / 100) +
                Math.round(((canvas.current.width * wordSize) / 100) * 0.85)
            );
          }
        });
      }
    }
  }, [gameSize.width, gameSize.height]);

  const WallGeneration = (rect) => {
    let img = patternHorImg.current;
    let h2 = canvas.current.height * (rect.h / 100);
    let w2 = (h2 * 1440) / 20;

    let aspect = 20 / (canvas.current.height * (rect.h / 100));

    if (rect.w < rect.h) {
      img = patternVerImg.current;
      w2 = canvas.current.width * (rect.w / 100);
      h2 = (w2 * 1420) / 20;

      aspect = 20 / (canvas.current.width * (rect.w / 100));
    }

    ctx.current.globalAlpha = Math.max(0, wallOpacity.current);
    ctx.current.drawImage(
      img,
      0,
      0,
      canvas.current.width * (rect.w / 100) * aspect,
      canvas.current.height * (rect.h / 100) * aspect,
      canvas.current.width * (rect.x / 100),
      canvas.current.height * (rect.y / 100),
      canvas.current.width * (rect.w / 100),
      canvas.current.height * (rect.h / 100)
    );
    ctx.current.globalAlpha = 1;

    ctx.current.fill();
  };

  return (
    <>
      {s == 0 && <WelcomeScreen />}
      {s == 1 && <LanguageScreen />}
      {s == 2 && <ExplScreen />}
      {s == 3 && <PickerScreen />}
      {s == 4 && (
        <div className="uiContainer">
          <Header />

          <div className="uiField">
            <canvas ref={canvas} />
          </div>

          <Footer />

          {gameStage == "to-start" && <LevelScreen level={l} />}
        </div>
      )}
      {s == 5 && <EndScreen type="lose" />}
      {s == 6 && <EndScreen type="win" />}

      <audio src="./sounds/sound1.mp3" ref={music1} />
      <audio src="./sounds/sound2.mp3" ref={music2} />
    </>
  );
};

export default Snake;
